import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTrialStatus } from '../../contexts/SubscriptionContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useConsultas } from '../../hooks/useConsultas';
import { ref, get, set } from '../../services/phpDataAdapter';
import { database } from '../../config/backend';
import './Layout.css';

interface UserProfile {
  name: string | null;
  clinic: string | null;
  clinicName: string | null;
}

export const Topbar: React.FC = () => {
  const { user, role, logout } = useAuth();
  const { daysRemaining, isTrial } = useTrialStatus();
  const { theme } = useTheme();
  const { consultas } = useConsultas(2); // Consultas de hoje e amanhã
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile>({
    name: null,
    clinic: null,
    clinicName: null,
  });
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isEditingClinic, setIsEditingClinic] = useState(false);
  const [editingClinicName, setEditingClinicName] = useState('');
  const [savingClinic, setSavingClinic] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const clinicInputRef = useRef<HTMLInputElement>(null);

  const notificationCount = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return consultas.filter((c) => {
      const consultaDate = new Date(c.dataIso);
      const consultaDay = new Date(
        consultaDate.getFullYear(),
        consultaDate.getMonth(),
        consultaDate.getDate()
      );
      return (
        consultaDay.getTime() === today.getTime() ||
        consultaDay.getTime() === tomorrow.getTime()
      );
    }).length;
  }, [consultas]);

  // Fecha o menu ao clicar fora dele
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    async function loadUserProfile() {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userSnap = await get(ref(database, `users/${user.uid}`));
        if (userSnap.exists()) {
          const userData = userSnap.val() as any;
          setProfile({
            name: userData.name || null,
            clinic: userData.clinic || null,
            clinicName: userData.clinicName || null,
          });
        }
      } catch (error) {
        console.error('Erro ao carregar perfil do usuário:', error);
      } finally {
        setLoading(false);
      }
    }

    loadUserProfile();
  }, [user]);

  const displayName =
    profile.name ||
    user?.displayName ||
    user?.email?.split('@')[0] ||
    'Nutricionista';

  const displayClinic = profile.clinicName || profile.clinic || 'Nutri clínica';

  const handleEditClinic = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingClinicName(displayClinic);
    setIsEditingClinic(true);
    setTimeout(() => {
      clinicInputRef.current?.focus();
    }, 0);
  };

  const handleSaveClinic = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.uid) return;
    
    setSavingClinic(true);
    try {
      await set(ref(database, `users/${user.uid}/clinicName`), editingClinicName.trim() || null);
      setProfile(prev => ({ ...prev, clinicName: editingClinicName.trim() || null }));
      setIsEditingClinic(false);
    } catch (error) {
      console.error('Erro ao salvar nome da clínica:', error);
      alert('Erro ao salvar nome da clínica');
    } finally {
      setSavingClinic(false);
    }
  };

  const handleCancelEditClinic = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingClinic(false);
    setEditingClinicName('');
  };

  const getInitials = () => {
    if (profile.name) {
      const names = profile.name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'N';
  };

  return (
    <header className="ns-topbar">
      <div className="ns-topbar-left">
        <h1 className="ns-topbar-title">Painel do nutricionista</h1>
        <p className="ns-topbar-subtitle">
          Acompanhe seus pacientes, planos e consultas em um só lugar.
        </p>
      </div>
      <div className="ns-topbar-right">
        {role === 'nutritionist' && isTrial && daysRemaining > 0 && (
          <Link
            to="/subscription"
            className="ns-topbar-trial-badge ns-topbar-trial-badge-clickable"
            title="Clique para gerenciar sua assinatura"
          >
            {daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'} restantes
          </Link>
        )}
        <Link
          to="/agenda"
          className="ns-topbar-notifications"
          aria-label="Notificações"
          title="Ver agenda"
        >
          🔔
          {notificationCount > 0 && (
            <span className="ns-topbar-notification-badge">
              {notificationCount > 9 ? '9+' : notificationCount}
            </span>
          )}
        </Link>
        <div 
          ref={userMenuRef}
          className="ns-topbar-user"
          onClick={() => setShowUserMenu(!showUserMenu)}
          style={{ cursor: 'pointer', position: 'relative' }}
        >
          <div className="ns-topbar-avatar">{getInitials()}</div>
          <div className="ns-topbar-user-info">
            <span className="ns-topbar-user-name">
              {loading ? 'Carregando...' : displayName}
            </span>
            {isEditingClinic ? (
              <div 
                className="ns-topbar-clinic-edit"
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}
              >
                <input
                  ref={clinicInputRef}
                  type="text"
                  value={editingClinicName}
                  onChange={(e) => setEditingClinicName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveClinic(e as any);
                    } else if (e.key === 'Escape') {
                      handleCancelEditClinic(e as any);
                    }
                  }}
                  style={{
                    padding: '2px 6px',
                    fontSize: '12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '4px',
                    width: '120px',
                    background: theme.colors.surface,
                    color: theme.colors.text,
                  }}
                  disabled={savingClinic}
                />
                <button
                  onClick={handleSaveClinic}
                  disabled={savingClinic}
                  style={{
                    padding: '2px 6px',
                    fontSize: '11px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: savingClinic ? 'not-allowed' : 'pointer',
                  }}
                  title="Salvar"
                >
                  ✓
                </button>
                <button
                  onClick={handleCancelEditClinic}
                  disabled={savingClinic}
                  style={{
                    padding: '2px 6px',
                    fontSize: '11px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: savingClinic ? 'not-allowed' : 'pointer',
                  }}
                  title="Cancelar"
                >
                  ✕
                </button>
              </div>
            ) : (
              <span 
                className="ns-topbar-user-role"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {loading ? '...' : displayClinic}
                <button
                  onClick={handleEditClinic}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    fontSize: '12px',
                    opacity: 0.6,
                  }}
                  title="Editar nome da clínica"
                >
                  ✏️
                </button>
              </span>
            )}
          </div>
          {showUserMenu && (
            <div className="ns-topbar-user-menu">
              <button
                className="ns-topbar-menu-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserMenu(false);
                  navigate('/configuracoes');
                }}
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: theme.colors.text,
                }}
              >
                ⚙️ Configurações
              </button>
              <button
                className="ns-topbar-logout-btn"
                onClick={async (e) => {
                  e.stopPropagation();
                  await logout();
                  setShowUserMenu(false);
                  navigate('/', { replace: true });
                }}
              >
                🚪 Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
